import React from "react";

// ── Types ──────────────────────────────────────────────────────────────────
interface PaymentMethod {
  bankName?: string;
  accountNumber?: string;
  accountName?: string;
}

interface Wallet {
  paymentMethod?: PaymentMethod;
}

interface BankAccountCardProps {
  wallet?: Wallet;
  loading?: boolean;
  onChangeAccount?: () => void;
  onLinkAccount?: () => void;
}

// ── Bank Icon SVG ──────────────────────────────────────────────────────────
const BankIcon: React.FC = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#fff"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="3" y1="22" x2="21" y2="22" />
    <line x1="6" y1="18" x2="6" y2="11" />
    <line x1="10" y1="18" x2="10" y2="11" />
    <line x1="14" y1="18" x2="14" y2="11" />
    <line x1="18" y1="18" x2="18" y2="11" />
    <polygon points="12 2 20 7 4 7" />
  </svg>
);

// ── Mask account number helper ─────────────────────────────────────────────
const maskAccount = (num: string): string => {
  if (num.length <= 6) return num;
  return `${num.slice(0, 2)}${"*".repeat(num.length - 6)}${num.slice(-4)}`;
};

const BankAccountCard: React.FC<BankAccountCardProps> = ({
  wallet,
  loading = false,
  onChangeAccount,
  onLinkAccount,
}) => {
  const pm = wallet?.paymentMethod;
  const hasAccount = pm?.bankName && pm?.accountNumber;
  const accountDisplay = hasAccount ? maskAccount(pm!.accountNumber!) : null;

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />
      <style>{`
        .bank-card-wrap {
          font-family: 'DM Sans', sans-serif;
          background: linear-gradient(135deg, #e8f5f0 0%, #d4ede6 100%);
          border-radius: 16px;
          padding: 20px 24px;
          display: flex;
          align-items: center;
          gap: 16px;
          width: 100%;
          border: none;
        }
        .bank-icon-box {
          width: 54px;
          height: 54px;
          min-width: 54px;
          background: #3bab8c;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .bank-info { flex: 1; min-width: 0; }
        .bank-name {
          font-size: 1rem;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .bank-number {
          font-size: 0.875rem;
          color: #4b5563;
          margin: 0;
        }
        .btn-change {
          background: #3bab8c;
          color: #fff;
          border: none;
          border-radius: 10px;
          padding: 10px 20px;
          font-size: 0.83rem;
          font-weight: 600;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          white-space: nowrap;
          transition: background 0.2s, transform 0.15s;
          flex-shrink: 0;
        }
        .btn-change:hover { background: #2e9e7a; transform: translateY(-1px); }
        .btn-change:active { transform: translateY(0); }
        .link-account {
          color: #3bab8c;
          font-weight: 700;
          font-size: 0.875rem;
          cursor: pointer;
          text-decoration: none;
          background: none;
          border: none;
          padding: 0;
          font-family: 'DM Sans', sans-serif;
        }
        .link-account:hover { text-decoration: underline; }
      `}</style>

      <div className="bank-card-wrap">
        {/* Bank icon */}
        <div className="bank-icon-box">
          <BankIcon />
        </div>

        {/* Account info */}
        <div className="bank-info">
          {loading ? (
            <p className="bank-number" style={{ color: "#9ca3af" }}>
              Loading…
            </p>
          ) : hasAccount ? (
            <>
              <p className="bank-name">{pm!.accountName ?? pm!.bankName}</p>
              <p className="bank-number">{accountDisplay}</p>
            </>
          ) : (
            <>
              <p className="bank-name">No account linked</p>
              <button className="link-account" onClick={onLinkAccount}>
                Link A Bank Account
              </button>
            </>
          )}
        </div>

        {/* Change account button — only when account exists */}
        {hasAccount && (
          <button className="btn-change" onClick={onChangeAccount}>
            Change account
          </button>
        )}
      </div>
    </>
  );
};

export default BankAccountCard;
